/**
 * Day resolution: from the published document to "what rings, and when".
 *
 * Valor runs Tuesday, Wednesday and Thursday. The other four days are home
 * days and must be completely silent — not a quiet schedule, no schedule. The
 * wings share the building and share nothing else: K-2 and 3-6 ring on their
 * own clocks, Upper (7–12) on its own, and none of them ever hears another's
 * lunch.
 *
 * The bell policy itself is not decided here. It is already encoded per band in
 * the document as `ringStart` / `ringEnd` / `ringBeforeEnd` / `warn`, because it
 * is the office's to change from the admin editor without shipping code. This
 * file only turns those flags into timed events:
 *
 *   ringStart     -> a bell at the band's start
 *   ringEnd       -> a bell at the band's end (Upper School: passing begins)
 *   ringBeforeEnd -> a bell N minutes before the band's end (Art / Choir:
 *                    "five minutes left"), null for every room that does not
 *                    want one, which is most of them
 *   warn          -> the one-minute song, exactly 60s before the band's start
 *
 * Everything downstream — the kiosk countdown, the tick that actually plays the
 * sound — reads the `DayPlan` this produces and nothing else.
 */

import type {
  Band,
  BellEvent,
  BellKind,
  DayOverride,
  DayPlan,
  DayStatus,
  Minutes,
  ScheduleDoc,
  Wing,
} from '../shared/types';
import { addDaysISO, chicagoDateISO, chicagoEpoch, chicagoParts, weekdayOfISO } from './time';

const MINUTES_IN_DAY = 1440;
const LAST_MINUTE_OF_DAY = MINUTES_IN_DAY - 1;

/** The song is one minute long and ends on the bell. */
const WARN_LEAD_MINUTES = 1;

/**
 * Two events of the same kind must never land on the same minute — a teacher
 * hears a stutter, not a schedule. When they collide, rank decides who speaks:
 * the band that is *starting* names the minute, because that is the label the
 * room needs next. Period 6's end and Dismissal's start are both 3:00; the
 * teacher should read "Dismissal".
 *
 * A "five minutes left" bell ranks below both edges: if it lands on a real
 * start or a real end, that band's own name is the more useful one to read, and
 * the bell rings either way.
 */
const RANK_START = 4;
const RANK_END = 3;
const RANK_BEFORE_END = 2;
const RANK_WARN = 1;

interface Candidate {
  kind: BellKind;
  at: Minutes;
  label: string;
  bandId: string;
  rank: number;
}

// ------------------------------------------------------------------- events

function buildEvents(bands: readonly Band[], dateISO: string): BellEvent[] {
  const candidates: Candidate[] = [];

  for (const band of bands) {
    if (band.ringStart) {
      candidates.push({
        kind: 'bell',
        at: band.start,
        label: band.label,
        bandId: band.id,
        rank: RANK_START,
      });

      // The song announces the bell that STARTS a band — "one minute until
      // class." Where a group has passing time that puts it inside the passing
      // period (9:24, a minute before the 9:25 bell), which is when students in
      // the corridor need it. Where a group has no passing it lands a minute
      // before the change (10:14 before the 10:15 bell).
      //
      // Anchoring to the start rather than the end is what makes the song mean
      // exactly one thing everywhere: a school bell is about to ring. It is
      // deliberately NOT played before the bell that ENDS a class and opens
      // passing — that bell is the cue to leave, and needs no warning.
      if (band.warn) {
        const at = band.start - WARN_LEAD_MINUTES;
        if (at >= 0) {
          candidates.push({ kind: 'warn', at, label: band.label, bandId: band.id, rank: RANK_WARN });
        }
      }
    }

    // Dismissal is an instant, not a span. It has no end to ring, and neither
    // does a band an edit has collapsed to nothing.
    const end = band.end;
    if (end === null || end <= band.start) continue;

    if (band.ringEnd) {
      candidates.push({ kind: 'bell', at: end, label: band.label, bandId: band.id, rank: RANK_END });
    }

    // "Five minutes left." Anchored to this band's own end rather than to the
    // next band's start, because the room this exists for — Art and Choir — is
    // where those two are not the same thing: the class in the room and the
    // class in the corridor are on different wings' clocks.
    const lead = band.ringBeforeEnd;
    if (lead != null && lead > 0) {
      const at = end - lead;
      // A lead longer than the band would ring before the class it is counting
      // down, and a lead of exactly the band's length would ring on top of its
      // own start bell. Neither is a warning, so neither rings.
      if (at > band.start) {
        candidates.push({
          kind: 'bell',
          at,
          label: band.label,
          bandId: band.id,
          rank: RANK_BEFORE_END,
        });
      }
    }
  }

  candidates.sort((a, b) => a.at - b.at || b.rank - a.rank);

  // A song must never land on the same minute as a bell. With short passing —
  // say two minutes — the song for the next class would otherwise sound on top
  // of the bell that ended the last one. The bell always wins; it is the signal
  // that actually moves the school.
  const bellMinutes = new Set(candidates.filter((c) => c.kind === 'bell').map((c) => c.at));

  const events: BellEvent[] = [];
  const claimed = new Set<string>();
  for (const candidate of candidates) {
    if (candidate.kind === 'warn' && bellMinutes.has(candidate.at)) continue;
    const minute = `${candidate.kind}@${candidate.at}`;
    if (claimed.has(minute)) continue;
    claimed.add(minute);
    events.push({
      kind: candidate.kind,
      at: candidate.at,
      atMs: chicagoEpoch(dateISO, candidate.at),
      label: candidate.label,
      bandId: candidate.bandId,
    });
  }

  // Minutes are sorted; instants are what the scheduler fires on. They only
  // disagree inside a DST gap, and the contract promises ascending atMs.
  events.sort((a, b) => a.atMs - b.atMs);
  return events;
}

// ---------------------------------------------------------------- overlays

/** A day the office pushed later. Nothing may be shoved past midnight. */
function applyDelay(bands: readonly Band[], delayMinutes: number): Band[] {
  const shifted: Band[] = [];
  for (const band of bands) {
    const start = band.start + delayMinutes;
    if (start < 0 || start > LAST_MINUTE_OF_DAY) continue;
    let end = band.end === null ? null : band.end + delayMinutes;
    if (end !== null && end > LAST_MINUTE_OF_DAY) end = LAST_MINUTE_OF_DAY;
    if (end !== null && end <= start) end = null;
    shifted.push({ ...band, start, end });
  }
  return shifted;
}

/**
 * "The office is dismissing at noon." Everything after the line goes; a band
 * that straddles it is cut short there; and the day is given a real dismissal
 * AT the line, because the whole point of the announcement is that a bell
 * rings at noon and the building empties.
 */
function applyEarly(bands: readonly Band[], wing: Wing, dismissAt: Minutes): Band[] {
  const kept: Band[] = [];
  for (const band of bands) {
    if (band.start >= dismissAt) continue;
    const end = band.end === null ? null : Math.min(band.end, dismissAt);
    kept.push({ ...band, end });
  }
  kept.push({
    id: `${wing}-early-dismissal-${dismissAt}`,
    label: 'Dismissal',
    kind: 'dismissal',
    start: dismissAt,
    end: null,
    ringStart: true,
    ringEnd: false,
    // Now that the song announces a band's START, an early dismissal gets one
    // too. A day being cut short is exactly when a minute of warning is worth
    // most — it is the least expected bell on the schedule.
    warn: true,
  });
  return kept;
}

function silentDay(dateISO: string, wing: Wing, status: DayStatus, note?: string): DayPlan {
  const plan: DayPlan = { date: dateISO, wing, isSchoolDay: false, status, bands: [], events: [] };
  if (note) plan.note = note;
  return plan;
}

// ------------------------------------------------------------------ the day

/**
 * The one entry point. Given the published document, a Chicago date and the
 * wing this tablet hangs in, produce everything that day will sound like.
 *
 * Resolution order matters and is deliberate:
 *   1. Is there school at all? Wrong weekday or outside the term is silence.
 *   2. The override for the date: closed wins outright; a replacement grid
 *      supplies the bands, and a delay or an early dismissal then reshapes
 *      whatever bands we ended up with.
 *   3. Turn the surviving bands into events at absolute instants.
 */
export function resolveDay(doc: ScheduleDoc, dateISO: string, wing: Wing): DayPlan {
  const override: DayOverride | undefined = doc.overrides[dateISO];

  // 1. Monday, Friday and the weekend are home days: not an empty schedule, no
  //    schedule. Same for any date outside the term.
  const onCalendar = doc.schoolDays.includes(weekdayOfISO(dateISO));
  const inTerm = dateISO >= doc.termStart && dateISO <= doc.termEnd;
  if (!onCalendar || !inTerm) return silentDay(dateISO, wing, 'normal', override?.note);

  // 2. Closed beats everything else the override could say.
  if (override?.status === 'closed') return silentDay(dateISO, wing, 'closed', override.note);

  const status: DayStatus = override?.status ?? 'normal';

  // A replacement grid is the source for the day; the status overlay still
  // applies on top of it, so "special assembly grid, and we start an hour late"
  // does what it says.
  // The bell group may not exist — the office can delete one, and a tablet
  // still signed in to it must fail quiet rather than throw. An empty day is
  // silent, which is safe; a crash would take the clock down with it.
  const source = override?.grids?.[wing] ?? doc.grids[wing]?.bands ?? [];
  let bands: Band[] = source.map((band) => ({ ...band }));

  if (status === 'delayed') {
    bands = applyDelay(bands, override?.delayMinutes ?? 0);
  } else if (status === 'early' && typeof override?.dismissAt === 'number') {
    bands = applyEarly(bands, wing, override.dismissAt);
  }

  bands.sort((a, b) => a.start - b.start);

  // 3.
  const plan: DayPlan = {
    date: dateISO,
    wing,
    isSchoolDay: true,
    status,
    bands,
    events: buildEvents(bands, dateISO),
  };
  if (override?.note) plan.note = override.note;
  return plan;
}

// ------------------------------------------------------------------ queries

/** The next thing that will ring. Strictly in the future: an event landing on
 *  this exact instant has just been fired by `dueEvents`, not queued. */
export function nextEvent(plan: DayPlan, nowMs: number): BellEvent | null {
  for (const event of plan.events) {
    if (event.atMs > nowMs) return event;
  }
  return null;
}

/** What the room is in the middle of, or null in a passing period, before the
 *  first band, or on a home day. Dismissal has no end, so it holds until
 *  midnight — the kiosk should still say "Dismissal" at 3:05. */
export function currentBand(plan: DayPlan, nowMs: number): Band | null {
  if (chicagoDateISO(nowMs) !== plan.date) return null;
  const minutes = chicagoParts(nowMs).minutes;
  for (const band of plan.bands) {
    const end = band.end ?? MINUTES_IN_DAY;
    if (minutes >= band.start && minutes < end) return band;
  }
  return null;
}

/**
 * How long the period in front of the teacher still has, on a lower-school wall.
 *
 * K–2 and 3–6 do not ring when a class ends. The next bell is often break,
 * lunch, or dismissal, while Math or Language Arts finishes long before that.
 * The wall still counts to this period's own end. Upper School and Art & Choir
 * return null and keep counting to the next bell. Dismissal has no end.
 */
export function periodRemaining(
  plan: DayPlan,
  nowMs: number,
  wing: Wing,
): { ms: number; label: string } | null {
  if (!isLowerSchoolGroup(wing)) return null;
  if (!plan.isSchoolDay) return null;
  const band = currentBand(plan, nowMs);
  if (!band || band.end == null) return null;
  const left = chicagoEpoch(plan.date, band.end) - nowMs;
  if (left <= 0) return null;
  return { ms: left, label: band.label };
}

/** Events strictly after fromMs and at-or-before toMs. Ascending.
 *  Half-open on purpose: the tick calls this with (lastTick, now], so no bell
 *  can be caught twice by two consecutive ticks. */
export function dueEvents(plan: DayPlan, fromMs: number, toMs: number): BellEvent[] {
  const due: BellEvent[] = [];
  for (const event of plan.events) {
    if (event.atMs > fromMs && event.atMs <= toMs) due.push(event);
  }
  return due;
}

/**
 * The next band that *starts* after now, whether or not it rings.
 *
 * Lower-school (K–6) class bands are silent on purpose. The wall still has to
 * say when the next period begins, so this is not `nextEvent`: it reads the
 * grid, not the bell list. Arrival is skipped: doors opening is not a period.
 */
export function nextPeriod(plan: DayPlan, nowMs: number): Band | null {
  if (!plan.isSchoolDay) return null;
  if (chicagoDateISO(nowMs) !== plan.date) return null;
  const minutes = chicagoParts(nowMs).minutes;
  for (const band of plan.bands) {
    if (band.kind === 'arrival') continue;
    if (band.start > minutes) return band;
  }
  return null;
}

/**
 * K–6: Primary used to be one group (`primary`); it is now two clocks
 * (`primary-lower`, `primary-upper`). Anything else — Upper School, Art &
 * Choir, a group the office invents — is not lower school.
 */
export function isLowerSchoolGroup(wing: Wing): boolean {
  return wing === 'primary' || wing.startsWith('primary-');
}

/**
 * The next date that actually rings, searching forward from (and not
 * including) `fromDateISO`. This is the Monday screen: rather than an empty
 * kiosk on a home day, point at the next morning that has bells. Home days,
 * closed days and dates past the end of term are skipped because they produce
 * no events at all.
 */
export function nextSchoolDay(
  doc: ScheduleDoc,
  fromDateISO: string,
  wing: Wing,
  maxLookaheadDays = 14,
): DayPlan | null {
  for (let offset = 1; offset <= maxLookaheadDays; offset += 1) {
    const plan = resolveDay(doc, addDaysISO(fromDateISO, offset), wing);
    if (plan.events.length > 0) return plan;
  }
  return null;
}
